import {
  buildDisplayWorkflowOps,
  recipeGroupsForStep,
  type RecipeGroupDef,
  type WoRowLite,
} from "./recipe-groups";

export type RecipeCells = Record<string, string>;

export type ParamMeta = {
  step: number;
  paramKey: string;
  label: string;
  unit?: string;
};

function maxStepFromCells(cells: RecipeCells): number {
  let max = -1;
  for (const k of Object.keys(cells)) {
    const m = /^(\d+)-/.exec(k);
    if (m) max = Math.max(max, Number(m[1]));
  }
  return max;
}

/** Ops for grouping / labels: workflow when present, else synthetic steps from cell keys. */
export function resolveCompareOps(wfOps: WoRowLite[], cells: RecipeCells): WoRowLite[] {
  const display = buildDisplayWorkflowOps(wfOps);
  if (display.length) return display;
  const n = maxStepFromCells(cells) + 1;
  if (n <= 0) return [];
  return Array.from({ length: n }, (_, i) => ({
    id: -(i + 1),
    processDefinitionName: `Step ${i + 1}`,
    mfgTechnologyName: "—",
    active: true as const,
  }));
}

/** Map `${step}-${paramKey}` → display meta from template groups. */
export function buildParamMetaByRowKey(ops: WoRowLite[]): Map<string, ParamMeta> {
  const map = new Map<string, ParamMeta>();
  ops.forEach((op, sIdx) => {
    const groups: RecipeGroupDef[] = recipeGroupsForStep(op);
    for (const g of groups) {
      for (const p of g.params) {
        const key = `${sIdx}-${p.key}`;
        map.set(key, {
          step: sIdx,
          paramKey: p.key,
          label: p.label,
          unit: p.unit,
        });
      }
    }
  });
  return map;
}

function collectRowKeys(cells: RecipeCells): Set<string> {
  const keys = new Set<string>();
  for (const k of Object.keys(cells)) {
    const m = /^(\d+)-([^-]+)-[tnx]$/.exec(k);
    if (m) keys.add(`${m[1]}-${m[2]}`);
  }
  return keys;
}

function formatTriplet(cells: RecipeCells, step: number, paramKey: string, unit?: string): string {
  const t = (cells[`${step}-${paramKey}-t`] ?? "").trim();
  const n = (cells[`${step}-${paramKey}-n`] ?? "").trim();
  const x = (cells[`${step}-${paramKey}-x`] ?? "").trim();
  const dash = "—";
  const a = t || dash;
  const b = n || dash;
  const c = x || dash;
  const u = (unit ?? "").trim();
  return u ? `${a} / ${b} / ${c} ${u}` : `${a} / ${b} / ${c}`;
}

export type CompareTableRow = {
  rowKey: string;
  /** e.g. "Step 1 · Spindle Speed" */
  parameter: string;
  valueA: string;
  valueB: string;
  different: boolean;
};

export function buildCompareRows(
  cellsA: RecipeCells,
  cellsB: RecipeCells,
  metaA: Map<string, ParamMeta>,
  metaB: Map<string, ParamMeta>,
): CompareTableRow[] {
  const keys = new Set<string>();
  collectRowKeys(cellsA).forEach((k) => keys.add(k));
  collectRowKeys(cellsB).forEach((k) => keys.add(k));
  const rows: CompareTableRow[] = [];
  keys.forEach((rowKey) => {
    const m = /^(\d+)-(.+)$/.exec(rowKey);
    if (!m) return;
    const step = Number(m[1]);
    const paramKey = m[2]!;
    const meta = metaA.get(rowKey) ?? metaB.get(rowKey);
    const label = meta?.label ?? paramKey;
    const unit = meta?.unit;
    const parameter = `Step ${step + 1} · ${label}`;
    const valueA = formatTriplet(cellsA, step, paramKey, unit);
    const valueB = formatTriplet(cellsB, step, paramKey, unit);
    const norm = (s: string) => s.replace(/\s+/g, " ").trim();
    const different = norm(valueA) !== norm(valueB);
    rows.push({ rowKey, parameter, valueA, valueB, different });
  });
  rows.sort((a, b) => {
    const sa = Number(/^(\d+)-/.exec(a.rowKey)?.[1] ?? 0);
    const sb = Number(/^(\d+)-/.exec(b.rowKey)?.[1] ?? 0);
    if (sa !== sb) return sa - sb;
    return a.parameter.localeCompare(b.parameter);
  });
  return rows;
}

export function summarizeTechnologies(ops: WoRowLite[]): string {
  const uniq = new Set<string>();
  for (const o of ops) {
    const v = (o.mfgTechnologyName ?? "").trim();
    if (v) uniq.add(v);
  }
  return Array.from(uniq).join(", ") || "—";
}

export function summarizeMachines(ops: WoRowLite[]): string {
  const uniq = new Set<string>();
  for (const o of ops) {
    const v = String((o as { machineName?: string | null }).machineName ?? "").trim();
    if (v) uniq.add(v);
  }
  return Array.from(uniq).join(", ") || "—";
}
